import { Popover2InteractionKind } from '@blueprintjs/popover2';
import { TrashCan, View } from '@carbon/icons-react';
import * as go from 'gojs';
import * as React from 'react';
import { useRouteMatch } from 'react-router-dom';
import {
  Body,
  MenuItem,
  MenuItems,
  OutlinedIconButton,
  Tooltip,
} from 'vet-bones/design-system/components';

import { SidebarCard } from 'src/components/sidebarCard/SidebarCard';
import { ROUTE } from 'src/constants/routes';
import {
  TEST_IDS,
  getProjectResourceCardTestId,
  getProjectResourceMenuButtonTestId,
} from 'src/constants/testIds';
import { useRemoveResource } from 'src/containers/ProjectDiagram/hooks/remove/useRemoveResource';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { commitDiagramWhileSkippingUndoManager } from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { getResourceKey } from 'src/containers/ProjectDiagram/utils/resource/createResource';
import { setDiagramSelection } from 'src/containers/ProjectDiagram/utils/setDiagramSelection';
import { VirtualGraphIcon } from 'src/containers/ProjectSidebar/components/VirtualGraphIcon';
import { currentStardogVersionSelector } from 'src/selectors/currentStardogVersion';
import { projectVirtualGraphSelector } from 'src/selectors/projectVirtualGraph';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

import cardStyles from 'src/components/sidebarCard/sidebarCard.module.scss';
import sidebarStyles from 'src/containers/ProjectSidebar/projectSidebar.module.scss';

const useProjectVirtualGraph = (virtualGraphId: string) => {
  const { projectId } = useRouterParams();
  return useReduxSelector(
    projectVirtualGraphSelector(projectId, virtualGraphId)
  );
};

type ProjectSidebarVirtualGraphCardProps = {
  idx: number | undefined;
  virtualGraphId: string;
};

export const ProjectSidebarVirtualGraphCard: React.VFC<
  ProjectSidebarVirtualGraphCardProps
> = ({ idx, virtualGraphId }) => {
  const diagram = useDiagram();
  const dispatch = useReduxDispatch();
  const isMappingRoute = Boolean(useRouteMatch(ROUTE.PROJECT_MAPPING));

  const virtualGraph = useProjectVirtualGraph(virtualGraphId);
  const stardogVersion = useReduxSelector(currentStardogVersionSelector);
  const handleDeleteVirtualGraph = useRemoveResource();

  const onClick = React.useCallback(() => {
    if (!diagram) {
      return;
    }

    const keysToSelect: go.Set<go.Part> = new go.Set();
    diagram.nodes.each((item) => {
      if (item.data.value === virtualGraphId) {
        keysToSelect.add(item);
      }
    });

    commitDiagramWhileSkippingUndoManager(diagram, (_, diagram) =>
      setDiagramSelection(keysToSelect)(diagram)
    );
  }, [diagram, virtualGraphId]);

  return (
    <SidebarCard
      actions={
        <Tooltip
          description={copy.components.project.resources.viewPreview}
          interactionKind={Popover2InteractionKind.HOVER_TARGET_ONLY}
          targetProps={{ className: cardStyles.sidebarCardMenu }}
        >
          <OutlinedIconButton
            leftIcon={View}
            onClick={() => {
              dispatch(
                uiSlice.actions.toggledVirtualGraphPreviewDialog({
                  isOpen: true,
                  resourceId: virtualGraphId,
                })
              );
            }}
            testId={TEST_IDS.projectSidebar.resourceMenu.viewPreviewButton}
          />
        </Tooltip>
      }
      menu={
        <MenuItems className={sidebarStyles.projectSidebarSmallMenuItems}>
          <MenuItem
            leftIcon={TrashCan}
            onClick={() =>
              handleDeleteVirtualGraph({
                key: getResourceKey(virtualGraphId),
              })
            }
            testId={TEST_IDS.projectSidebar.resourceMenu.deleteButton}
            text={copy.components.project.resources.delete}
          />
        </MenuItems>
      }
      menuTestId={getProjectResourceMenuButtonTestId(virtualGraphId, idx)}
      onClick={isMappingRoute ? undefined : onClick}
      testId={getProjectResourceCardTestId(virtualGraphId, idx)}
      title={
        <>
          <VirtualGraphIcon
            dataSourceType={virtualGraph?.dataSourceType}
            stardogVersion={stardogVersion}
          />
          <Body
            ellipsize
            size="md"
            testId={TEST_IDS.projectSidebar.resourceCard.title}
          >
            {virtualGraph?.name || virtualGraphId}
          </Body>
        </>
      }
    />
  );
};
